import React from 'react';
import useReactRouter from 'use-react-router';
import { gql } from '@apollo/client';
import Button from 'jbc-front/components/Button';
import { LabelMapper } from 'jbc-front/components/FormErrors';
import { BoxDouble, Section } from 'jbc-front/components/Form';
import { LoadingPage, reduxForm, useFormValue, useMutation, useQuery } from '../components';
import AutoField from '../components/AutoField';
import { AmountField, FormSection } from '../components/FieldWithDiff';
import { Buttons, Headding, Item, Lead, Panel } from '../components/PrimaryPanel';
import { CustomDescription } from '../components/QuestionDescription';
import { toFormValues } from '../utils';
import { salaryEarnings, calcTotalSalaryIncome, parseNumber } from '../utils/income';
import { INCOME_FRAGMENT } from './Spouse';
import { amount } from '../validators';
import { numberWithSplit } from '../utils/recordDisplay';
import { ToggleDivImage } from '../components/ToggleDivImage';
import image from '../../images/img-Foundation_Exclusion_Form.png';
import styles from './AllIncome.scss';
import { amountFormat } from '@jbc-year-end-adj/common/utils/formatter';

const INCOME = gql`
  query income {
    request {
      id
      employee {
        id
        year
      }
      profile {
        id
        birthday
        income {
          ...Income
        }
        formerJobs {
          id
          paymentAmount
        }
      }
    }
  }
  ${INCOME_FRAGMENT}
`;

const ANSWER_SALARY_INCOME = gql`
  mutation answerSalaryIncome($income: IncomeInput!) {
    answerSalaryIncome(input: { income: $income }) {
      questions {
        id
        status
      }
      profile {
        id
        income {
          ...Income
        }
      }
    }
  }
  ${INCOME_FRAGMENT}
`;

export const PersonalSalaryIncome = ({ field, year, formerJobs }) => {
  const { mainJobIncome, sideJobIncome } = useFormValue(field);
  const formerJobsPaymentAmount = numberWithSplit(
    formerJobs
      .map(v => v.paymentAmount)
      .map(v => parseNumber(v))
      .reduce((previous, current) => previous + current, 0)
  );
  const totalSalaryIncome = calcTotalSalaryIncome(mainJobIncome, sideJobIncome, formerJobsPaymentAmount);

  return (
    <FormSection name="income">
      <BoxDouble>
        <AmountField
          name="mainJobIncome"
          label="給与所得 収入金額（当社）"
          validate={amount}
          description="前Stepで入力した前職情報は含みません"
          yen
        />
        <AmountField
          name="sideJobIncome"
          label="給与所得 収入金額（他社）"
          description="前Stepで入力した前職情報は含みません"
          validate={amount}
          yen
        />
        <AutoField label="給与所得 収入金額（前職）" value={formerJobsPaymentAmount} yen />
      </BoxDouble>
      <BoxDouble>
        <AutoField label="給与所得 収入金額（合計）" value={totalSalaryIncome} yen />
        <AutoField label="給与所得 所得金額" value={salaryEarnings(totalSalaryIncome, year)} yen />
      </BoxDouble>
    </FormSection>
  );
};

const QuestionForm =
  (({ submitting, handleSubmit, year, title, formerJobs }) => {
    return (
      <form onSubmit={handleSubmit}>
        <Item>
          <Lead>本人の給与収入欄を入力してください</Lead>
          <CustomDescription field="top" />
          <ToggleDivImage title={'「基礎控除申告書（赤枠）」に反映されます。'}>
            <img src={image} alt="基配所_基礎控除申告書" />
          </ToggleDivImage>
          <>
            <div className={styles.redDescription}>
              「当社」欄は今年1年間の見込み収入金額（賞与含む、前職除く）を入力してください。
              <br />
              「他社」欄は2か所以上からの給与収入がある場合にその金額を入力してください。
            </div>
            <Section title={'本人の給与収入'} style={{ maxWidth: '760px' }}>
              <LabelMapper name="income" label="本人の収入の" />
              <PersonalSalaryIncome formerJobs={formerJobs} field="income" year={year} />
            </Section>
            <Buttons>
              <Button primary onClick={handleSubmit} disabled={submitting}>
                次ヘ
              </Button>
            </Buttons>
          </>
        </Item>
      </form>
    );
  }) |> reduxForm({ form: 'salaryIncome' });

const makeInitialValues = profile => {
  const income = toFormValues(profile.income, true);
  return { income };
};

const Question = ({ questionMap }) => {
  const { loading, data } = useQuery(INCOME);
  const [answer] = useMutation(ANSWER_SALARY_INCOME);
  const { history } = useReactRouter();
  if (loading) return <LoadingPage />;
  const {
    request: {
      profile,
      employee: { year }
    }
  } = data;
  const formatValues = profile => {
    return {
      ...profile,
      income: {
        ...profile.income,
        mainJobIncome: amountFormat(profile.income.mainJobIncome),
        sideJobIncome: amountFormat(profile.income.sideJobIncome)
      }
    };
  };

  return (
    <Panel>
      <Headding className={styles.primaryPanelHeader}>給与収入等（本人の給与収入）</Headding>
      <QuestionForm
        initialValues={makeInitialValues(formatValues(profile))}
        onSubmit={async values => {
          await answer({ variables: { income: values.income } });
          history.push('/employee_input');
        }}
        year={year}
        formerJobs={profile.formerJobs}
      />
    </Panel>
  );
};

export default Question;
